import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

const variants = [
  {
    id: "monolith",
    n: "01",
    name: "Monolith",
    tag: "Editorial · Brutalist",
    desc: "FT-meets-print magazine. Massive slab typography, bone-white on jet black, vertical cropped rock, ruled grid lines and editorial column flow.",
    img: "https://images.pexels.com/photos/12184310/pexels-photo-12184310.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720",
  },
  {
    id: "obsidian",
    n: "02",
    name: "Obsidian",
    tag: "Cinematic · Glass",
    desc: "Frosted glass dashboard cards floating over a slow-rotating obsidian core. Live rate ticker, animated counters, scroll-driven horizontal panels.",
    img: "https://images.unsplash.com/photo-1634382851346-890ee66f08b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxibGFjayUyMHN0b25lJTIwbW9ub2xpdGh8ZW58MHx8fGJsYWNrfDE3NzcyMTczMjd8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "bedrock",
    n: "03",
    name: "Bedrock",
    tag: "Swiss · Data Grid",
    desc: "Strict 12-column Swiss grid. Centered rotating monolith with radial labels, mono numerals, justified columns and a dense data table aesthetic.",
    img: "https://images.unsplash.com/photo-1712730324756-e4879e9c9b11?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxkYXJrJTIwZ3JleSUyMHNsYXRlJTIwc3RvbmUlMjB0ZXh0dXJlfGVufDB8fHx8MTc3NzIxNzMzOXww&ixlib=rb-4.1.0&q=85",
  },
];

export default function Designs() {
  return (
    <main
      className="min-h-screen bg-[#050505] text-[#F2F0EB] grain"
      data-testid="designs-page"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 py-20">
        <div className="flex items-start justify-between gap-8 mb-20">
          <div>
            <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.35em] uppercase text-[#8C8C8C] mb-6">
              <span className="w-8 h-[1px] bg-[#404040]" />
              <span>Three Directions</span>
            </div>
            <h1 className="font-display uppercase text-[#F2F0EB] leading-[0.92] tracking-tight text-5xl sm:text-6xl lg:text-7xl">
              Pick a stance.
              <br />
              <span className="text-[#F2F0EB]/55">Same brand, three voices.</span>
            </h1>
            <p className="mt-6 font-body text-sm sm:text-base text-[#8C8C8C] max-w-xl leading-relaxed">
              Each route below is a full, working landing page in the
              Credit&nbsp;Stone universe — fintech, sleek, black-rock. Open one,
              live with it for thirty seconds, decide.
            </p>
          </div>
          <Link
            to="/"
            className="hidden md:flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase px-5 py-2.5 rounded-full border border-white/15 hover:bg-white/5 transition-colors whitespace-nowrap"
            data-testid="back-to-original"
          >
            Original ↗
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {variants.map((v, i) => (
            <motion.div
              key={v.id}
              initial={{ y: 40, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease, delay: i * 0.12 }}
            >
              <Link
                to={`/design/${v.id}`}
                className="group block relative overflow-hidden rounded-2xl border border-[#1A1A1A] bg-[#0A0A0A] hover:border-[#2E2E2E] transition-colors"
                data-testid={`variant-card-${v.id}`}
              >
                <div className="aspect-[4/5] w-full overflow-hidden relative">
                  <img
                    src={v.img}
                    alt={v.name}
                    className="w-full h-full object-cover grayscale contrast-110 transition-transform duration-[1400ms] group-hover:scale-105"
                    draggable="false"
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(5,5,5,0.95) 100%)",
                    }}
                  />
                  <div className="absolute top-6 left-6 right-6 flex items-start justify-between">
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F2F0EB]/80">
                      {v.n} / 03
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F2F0EB]/80">
                      {v.tag}
                    </div>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="font-display uppercase text-4xl sm:text-5xl text-[#F2F0EB] leading-none">
                      {v.name}
                    </div>
                    <p className="mt-3 font-body text-sm text-[#8C8C8C] max-w-[28ch] leading-relaxed">
                      {v.desc}
                    </p>
                    <div className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-[#F2F0EB] group-hover:gap-3 transition-all">
                      Open route
                      <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 font-mono text-[10px] tracking-[0.3em] uppercase text-[#5a5a5a]">
          Routes — /design/monolith · /design/obsidian · /design/bedrock
        </div>
      </div>
    </main>
  );
}
